import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ITEMS, PNL_FLAG_KEY } from './OnboardingChecklist';
import VideoLink from './VideoLink';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
const GOLD = '#c9a227';
const COLLAPSE_KEY = 'scn-setup-widget-collapsed';
const MINIMIZE_KEY = 'scn-setup-widget-minimized';

// Persistent, collapsible "Complete Setup" activation widget (onboarding-checklist pattern).
// Lives bottom-left on every in-app page (chat bubble owns bottom-right). Reuses
// the shared ITEMS + the /onboarding/checklist flags. Collapsed by default to a
// small pill; expands on click. Vanishes for good when all steps are done.
export default function SetupWidget() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(() => {
    // Start CLOSED (the small pill) so a brand-new user's dashboard isn't
    // dominated by an 8-row checklist, that overwhelm is the churn-bucket-1
    // problem. Only auto-open if they've explicitly opened it before
    // (COLLAPSE_KEY === '0'); otherwise they tap the pill to expand.
    try { return localStorage.getItem(COLLAPSE_KEY) === '0'; } catch { return false; }
  });
  // Minimized = shrunk to a small progress ring, fully out of the way. Persists.
  const [minimized, setMinimized] = useState(() => {
    try { return localStorage.getItem(MINIMIZE_KEY) === '1'; } catch { return false; }
  });
  const setMin = (v) => { setMinimized(v); try { localStorage.setItem(MINIMIZE_KEY, v ? '1' : '0'); } catch {} };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API}/onboarding/checklist`);
        if (cancelled) return;
        setStatus({ ...res.data, pnlViewed: localStorage.getItem(PNL_FLAG_KEY) === '1' });
      } catch {
        if (!cancelled) setStatus({});
      }
    }
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, []);

  const toggle = () => {
    setOpen(o => {
      const next = !o;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '0' : '1'); } catch {}
      return next;
    });
  };

  if (status === null) return null; // loading
  const items = ITEMS.map(i => ({ ...i, done: !!status[i.id] }));
  const doneCount = items.filter(i => i.done).length;
  if (doneCount === items.length) return null; // all done, gone for good
  const pct = Math.round((doneCount / items.length) * 100);

  // Minimized: just a small progress ring tucked in the corner. Click to restore.
  if (minimized) {
    return (
      <div className="setup-widget minimized">
        <button className="setup-widget-mini" onClick={() => setMin(false)} title={`Complete Setup, ${doneCount} of ${items.length} done`} aria-label="Show setup checklist">
          <Ring pct={pct} />
        </button>
      </div>
    );
  }

  return (
    <div className="setup-widget">
      {/* Minimize to the corner ring (out of the way, one tap to bring back). */}
      <button className="setup-widget-minbtn" onClick={() => setMin(true)} title="Minimize" aria-label="Minimize setup checklist">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
      {/* Collapsed pill (always rendered as the toggle header) */}
      <button className="setup-widget-pill" onClick={toggle} aria-expanded={open}>
        <Ring pct={pct} />
        <span className="setup-widget-pill-text">
          <b>Complete Setup</b>
          <span>{doneCount} of {items.length} done</span>
        </span>
        <svg className="setup-widget-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {open && (
        <div className="setup-widget-body">
          {items.map(item => (
            <Link key={item.id} to={item.href} className={`setup-widget-item${item.done ? ' done' : ''}`}>
              <Check done={item.done} />
              <span className="setup-widget-item-text">
                <span className="t">{item.title}</span>
                {!item.done && item.videoUrl && (
                  <VideoLink src={item.videoUrl} label="▶ Watch video" className="setup-widget-video" />
                )}
              </span>
              {!item.done && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9aa1ad" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Ring({ pct }) {
  const r = 9, c = 2 * Math.PI * r;
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
      <circle cx="12" cy="12" r={r} fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} transform="rotate(-90 12 12)" />
    </svg>
  );
}

function Check({ done }) {
  if (done) return (
    <span className="setup-widget-check done">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
    </span>
  );
  return <span className="setup-widget-check" />;
}
